'use client';

import { Menu as MuiMenu } from '@mui/material';
import { MenuGrowTransition } from '../MenuGrowTransition/MenuGrowTransition';

/** Side of the arrow's square, in px. */
export const MENU_ARROW_SIZE = 10;

/** Which edge of the menu lines up with the same edge of the anchor; `center` centres the menu under it. */

const HORIZONTAL = {
  start: 'left',
  center: 'center',
  end: 'right'
};
function anchorWidth(anchorEl) {
  const el = typeof anchorEl === 'function' ? anchorEl() : anchorEl;
  return el?.getBoundingClientRect().width ?? 0;
}

/** Custom properties that place the arrow under the anchor's middle, for the theme's `&[data-arrow]::before`. */
function arrowStyle(arrow, anchorEl) {
  const offset = `${Math.max(0, anchorWidth(anchorEl) / 2 - MENU_ARROW_SIZE / 2)}px`;
  const vars = {
    '--menu-arrow-size': `${MENU_ARROW_SIZE}px`
  };
  if (arrow === 'start') vars['--menu-arrow-left'] = offset;else if (arrow === 'end') vars['--menu-arrow-right'] = offset;else vars['--menu-arrow-left'] = `calc(50% - ${MENU_ARROW_SIZE / 2}px)`;
  return vars;
}

/** Keeps the arrow this far from the paper's corners when it has to move. */
const ARROW_EDGE_INSET = 12;

/**
 * Re-points the arrow at the anchor's middle once the paper is placed. Only matters when MUI pushed the paper inward
 * to keep it on screen; otherwise it lands where the render-time offset already put it. Reads `offsetLeft`/`offsetWidth`
 * (not the bounding rect) so the grow transition's scale doesn't skew the measurement.
 */
function alignArrowToAnchor(paper, anchorEl) {
  const el = typeof anchorEl === 'function' ? anchorEl() : anchorEl;
  if (!el || !paper.offsetWidth) return;
  const anchorRect = el.getBoundingClientRect();
  const center = anchorRect.left + anchorRect.width / 2 - paper.offsetLeft;
  const clamped = Math.min(Math.max(center, ARROW_EDGE_INSET), paper.offsetWidth - ARROW_EDGE_INSET);
  paper.style.setProperty('--menu-arrow-left', `${clamped - MENU_ARROW_SIZE / 2}px`);
  paper.style.removeProperty('--menu-arrow-right');
}

/** MUI `Menu` plus an optional `arrow`. Without `arrow` it is exactly MUI's Menu. */
export function Menu({
  arrow,
  anchorEl,
  anchorOrigin,
  transformOrigin,
  slots,
  slotProps,
  ...rest
}) {
  if (!arrow) {
    return <MuiMenu anchorEl={anchorEl} anchorOrigin={anchorOrigin} transformOrigin={transformOrigin} slots={slots} slotProps={slotProps} {...rest} />;
  }
  const horizontal = HORIZONTAL[arrow];
  const style = arrowStyle(arrow, anchorEl);
  const withArrow = paper => ({
    ...paper,
    'data-arrow': arrow,
    style: {
      ...paper?.style,
      ...style
    }
  });
  const paper = slotProps?.paper;
  const transition = slotProps?.transition;
  return <MuiMenu anchorEl={anchorEl} anchorOrigin={anchorOrigin ?? {
    vertical: 'bottom',
    horizontal
  }} transformOrigin={transformOrigin ?? {
    vertical: 'top',
    horizontal
  }} slots={{
    transition: MenuGrowTransition,
    ...slots
  }} slotProps={{
    ...slotProps,
    paper: typeof paper === 'function' ? ownerState => withArrow(paper(ownerState)) : withArrow(paper),
    transition: {
      ...transition,
      onEntering: (node, isAppearing) => {
        transition?.onEntering?.(node, isAppearing);
        // After Popover has positioned the paper for this frame.
        requestAnimationFrame(() => alignArrowToAnchor(node, anchorEl));
      }
    }
  }} {...rest} />;
}